import { useState, useEffect, useRef, useCallback } from 'react'
import {
  Box,
  Typography,
  Button,
  ButtonBase,
  IconButton,
  CircularProgress,
} from '@mui/material'
import { SkipNext, Cancel, Close, Send, Backspace, Lock, RestartAlt, LockOpen } from '@mui/icons-material'
import { SessionNodeDto, PendingInputDto } from '@api/types/session'
import { NumpadInputStyle } from '../../../types/workflow.types'
import { useSessionInput } from '../../../hooks/useSessionInput'
import { useFlickInertia } from '../../../hooks/useFlickInertia'

interface NumpadInputProps {
  node: SessionNodeDto
  pendingInput: PendingInputDto
  sessionId: string | null
  onSubmitSuccess?: () => void
  onClose?: () => void
}

export default function NumpadInput({
  node,
  pendingInput,
  sessionId,
  onSubmitSuccess,
  onClose,
}: NumpadInputProps) {
  const [code, setCode] = useState('')
  const hasCalledSuccessRef = useRef(false)

  const { submitNumpad, skipChallenge, cancelChallenge, isSubmitting, isSuccess } = useSessionInput(sessionId)

  useEffect(() => {
    if (isSuccess && onSubmitSuccess && !hasCalledSuccessRef.current) {
      hasCalledSuccessRef.current = true
      onSubmitSuccess()
    }
  }, [isSuccess, onSubmitSuccess])

  const digitCount = node.digitCount || 4
  const inputStyle = node.inputStyle as NumpadInputStyle | undefined

  const handleKeyPress = (digit: string) => {
    if (code.length < digitCount) {
      setCode((prev) => prev + digit)
    }
  }

  const handleBackspace = () => {
    setCode((prev) => prev.slice(0, -1))
  }

  const handleSubmit = () => {
    if (!node.id || code.length !== digitCount) return
    submitNumpad(node.id, code)
  }

  const handleSkip = () => {
    if (!node.id || !pendingInput.allowSkip) return
    skipChallenge(node.id, pendingInput.inputType)
  }

  const handleCancel = () => {
    if (!node.id || !pendingInput.allowCancel) return
    cancelChallenge(node.id, pendingInput.inputType)
  }

  const canSubmit = code.length === digitCount
  const questionText = node.questionText || node.description || ''

  // Free input style - simple rounded input row
  if (inputStyle === NumpadInputStyle.FreeInput) {
    return (
      <Box
        sx={{
          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.paper' : 'background.default',
          borderTop: 1,
          borderColor: 'divider',
          p: 2,
          position: 'relative',
        }}
      >
        {onClose && (
          <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', top: 4, right: 4, color: 'text.disabled' }}>
            <Close fontSize="small" />
          </IconButton>
        )}

        {questionText && (
          <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.6, color: 'text.secondary', mb: 1.5, pr: onClose ? 4 : 0 }}>
            {questionText}
          </Typography>
        )}

        <Typography
          sx={{
            fontSize: '0.6875rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.8px',
            color: 'text.disabled',
            mb: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
          }}
        >
          🔢 Enter Number
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box
            component="input"
            type="text"
            inputMode="numeric"
            value={code}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const val = e.target.value.replace(/\D/g, '').slice(0, digitCount)
              setCode(val)
            }}
            onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' && canSubmit) handleSubmit() }}
            placeholder="Enter number..."
            disabled={isSubmitting}
            sx={{
              flex: 1,
              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(21,32,58,0.6)' : 'rgba(0,0,0,0.04)',
              border: 1,
              borderColor: 'divider',
              borderRadius: '24px',
              px: 2.5,
              py: 1.5,
              fontSize: '1.125rem',
              fontWeight: 600,
              color: 'text.primary',
              fontFamily: "'Inter', sans-serif",
              textAlign: 'center',
              letterSpacing: '2px',
              fontVariantNumeric: 'tabular-nums',
              outline: 'none',
              '&:focus': {
                borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(212,168,83,0.4)' : 'primary.main',
              },
              '&::placeholder': {
                color: 'text.disabled',
                fontWeight: 400,
                letterSpacing: 0,
                fontSize: '0.875rem',
              },
            }}
          />

          <ButtonBase
            onClick={handleSubmit}
            disabled={!canSubmit || isSubmitting}
            sx={{
              width: 46,
              height: 46,
              borderRadius: '50%',
              background: canSubmit
                ? 'linear-gradient(135deg, #d4a853, #c4943f)'
                : 'rgba(128,128,128,0.2)',
              color: canSubmit ? '#1a1a2e' : 'text.disabled',
              flexShrink: 0,
              boxShadow: canSubmit ? '0 2px 8px rgba(212,168,83,0.3)' : 'none',
              transition: 'all 0.15s',
            }}
          >
            {isSubmitting ? <CircularProgress size={20} sx={{ color: '#1a1a2e' }} /> : <Send sx={{ fontSize: 18 }} />}
          </ButtonBase>
        </Box>

        {(pendingInput.allowCancel || pendingInput.allowSkip) && (
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 1.5 }}>
            {pendingInput.allowCancel && (
              <Button size="small" startIcon={<Cancel />} onClick={handleCancel} disabled={isSubmitting} sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                {pendingInput.cancelButtonText || 'Cancel'}
              </Button>
            )}
            {pendingInput.allowSkip && (
              <Button size="small" startIcon={<SkipNext />} onClick={handleSkip} disabled={isSubmitting} sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                {pendingInput.skipButtonText || 'Skip'}
              </Button>
            )}
          </Box>
        )}
      </Box>
    )
  }

  // Roller digits style — combination lock
  if (inputStyle === NumpadInputStyle.RollerDigits) {
    return (
      <RollerDigitsInput
        node={node}
        pendingInput={pendingInput}
        digitCount={digitCount}
        questionText={questionText}
        isSubmitting={isSubmitting}
        submitNumpad={submitNumpad}
        skipChallenge={skipChallenge}
        cancelChallenge={cancelChallenge}
        onClose={onClose}
      />
    )
  }

  // 10-key pad style (default)
  return (
    <Box
      sx={{
        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.paper' : 'background.default',
        borderTop: 1,
        borderColor: 'divider',
        p: 2,
        position: 'relative',
      }}
    >
      {onClose && (
        <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', top: 4, right: 4, color: 'text.disabled' }}>
          <Close fontSize="small" />
        </IconButton>
      )}

      {questionText && (
        <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.6, color: 'text.secondary', mb: 1.5, pr: onClose ? 4 : 0 }}>
          {questionText}
        </Typography>
      )}

      <Typography
        sx={{
          fontSize: '0.6875rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.8px',
          color: 'text.disabled',
          mb: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
        }}
      >
        🔢 Enter Code
      </Typography>

      {/* Code display */}
      <Box
        sx={{
          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(21,32,58,0.6)' : 'rgba(0,0,0,0.04)',
          border: 1,
          borderColor: 'divider',
          borderRadius: 3,
          px: 2.5,
          py: 1.75,
          mb: 1.75,
          textAlign: 'center',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        <Typography
          sx={{
            fontSize: '2rem',
            fontWeight: 700,
            letterSpacing: '12px',
            color: 'text.primary',
            fontFamily: "'Inter', sans-serif",
            display: 'inline',
          }}
        >
          {code}
        </Typography>
        {/* Placeholder dots for remaining digits */}
        <Typography
          component="span"
          sx={{
            fontSize: '2rem',
            fontWeight: 700,
            letterSpacing: '12px',
            color: 'text.disabled',
            opacity: 0.3,
          }}
        >
          {'●'.repeat(Math.max(0, digitCount - code.length))}
        </Typography>
        {/* Blinking cursor */}
        {code.length < digitCount && (
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              width: 2,
              height: 28,
              bgcolor: 'primary.main',
              verticalAlign: 'middle',
              ml: 0.5,
              animation: 'blink 1s step-end infinite',
              '@keyframes blink': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0 },
              },
            }}
          />
        )}
      </Box>

      {/* Numpad grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 1,
          maxWidth: 280,
          mx: 'auto',
          mb: 1.25,
        }}
      >
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => (
          <ButtonBase
            key={digit}
            onClick={() => handleKeyPress(digit)}
            disabled={code.length >= digitCount || isSubmitting}
            sx={{
              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.default' : 'background.paper',
              border: 1,
              borderColor: 'divider',
              borderRadius: 3,
              color: 'text.primary',
              fontSize: '1.375rem',
              fontWeight: 600,
              py: 1.75,
              fontFamily: "'Inter', sans-serif",
              transition: 'all 0.1s',
              '&:active': {
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                transform: 'scale(0.96)',
              },
              '&.Mui-disabled': {
                color: 'text.disabled',
                opacity: 0.4,
              },
            }}
          >
            {digit}
          </ButtonBase>
        ))}

        {/* Backspace */}
        <ButtonBase
          onClick={handleBackspace}
          disabled={code.length === 0 || isSubmitting}
          sx={{
            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(252,129,129,0.1)' : 'rgba(244,67,54,0.06)',
            border: 1,
            borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(252,129,129,0.2)' : 'rgba(244,67,54,0.15)',
            borderRadius: 3,
            color: 'error.main',
            fontSize: '1rem',
            py: 1.75,
            transition: 'all 0.1s',
            '&:active': { transform: 'scale(0.96)' },
          }}
        >
          <Backspace sx={{ fontSize: 20 }} />
        </ButtonBase>

        {/* 0 */}
        <ButtonBase
          onClick={() => handleKeyPress('0')}
          disabled={code.length >= digitCount || isSubmitting}
          sx={{
            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.default' : 'background.paper',
            border: 1,
            borderColor: 'divider',
            borderRadius: 3,
            color: 'text.primary',
            fontSize: '1.375rem',
            fontWeight: 600,
            py: 1.75,
            fontFamily: "'Inter', sans-serif",
            transition: 'all 0.1s',
            '&:active': {
              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
              transform: 'scale(0.96)',
            },
          }}
        >
          0
        </ButtonBase>

        {/* Submit */}
        <ButtonBase
          onClick={handleSubmit}
          disabled={!canSubmit || isSubmitting}
          sx={{
            background: canSubmit
              ? 'linear-gradient(135deg, #d4a853, #c4943f)'
              : 'rgba(128,128,128,0.15)',
            border: 'none',
            borderRadius: 3,
            color: canSubmit ? '#1a1a2e' : 'text.disabled',
            fontSize: '0.875rem',
            fontWeight: 700,
            py: 1.75,
            fontFamily: "'Inter', sans-serif",
            boxShadow: canSubmit ? '0 2px 8px rgba(212,168,83,0.3)' : 'none',
            transition: 'all 0.15s',
          }}
        >
          {isSubmitting ? <CircularProgress size={20} sx={{ color: '#1a1a2e' }} /> : 'Submit'}
        </ButtonBase>
      </Box>

      {/* Digit count label */}
      <Typography
        sx={{
          textAlign: 'center',
          fontSize: '0.6875rem',
          color: 'text.disabled',
          mb: 0.5,
        }}
      >
        {digitCount} digits required
      </Typography>

      {/* Skip / Cancel */}
      {(pendingInput.allowCancel || pendingInput.allowSkip) && (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 1 }}>
          {pendingInput.allowCancel && (
            <Button size="small" startIcon={<Cancel />} onClick={handleCancel} disabled={isSubmitting} sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
              {pendingInput.cancelButtonText || 'Cancel'}
            </Button>
          )}
          {pendingInput.allowSkip && (
            <Button size="small" startIcon={<SkipNext />} onClick={handleSkip} disabled={isSubmitting} sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
              {pendingInput.skipButtonText || 'Skip'}
            </Button>
          )}
        </Box>
      )}
    </Box>
  )
}

// --- Roller Digits (Combination Lock) ---

interface RollerDigitsInputProps {
  node: SessionNodeDto
  pendingInput: PendingInputDto
  digitCount: number
  questionText: string
  isSubmitting: boolean
  submitNumpad: (nodeId: number, code: string) => void
  skipChallenge: (nodeId: number, inputType: number) => void
  cancelChallenge: (nodeId: number, inputType: number) => void
  onClose?: () => void
}

function RollerDigitsInput({
  node,
  pendingInput,
  digitCount,
  questionText,
  isSubmitting,
  submitNumpad,
  skipChallenge,
  cancelChallenge,
  onClose,
}: RollerDigitsInputProps) {
  const [digits, setDigits] = useState<number[]>(() => Array(digitCount).fill(0))
  const [hasInteracted, setHasInteracted] = useState(false)

  const setDigitAt = useCallback((index: number, value: number) => {
    setHasInteracted(true)
    setDigits((prev) => {
      const next = [...prev]
      next[index] = value
      return next
    })
  }, [])

  const handleReset = () => {
    setDigits(Array(digitCount).fill(0))
    setHasInteracted(false)
  }

  const handleSubmit = () => {
    if (!node.id || !hasInteracted) return
    submitNumpad(node.id, digits.join(''))
  }

  const handleSkip = () => {
    if (!node.id || !pendingInput.allowSkip) return
    skipChallenge(node.id, pendingInput.inputType)
  }

  const handleCancel = () => {
    if (!node.id || !pendingInput.allowCancel) return
    cancelChallenge(node.id, pendingInput.inputType)
  }

  return (
    <Box
      sx={{
        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.paper' : 'background.default',
        borderTop: 1,
        borderColor: 'divider',
        p: 2,
        position: 'relative',
      }}
    >
      {onClose && (
        <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', top: 4, right: 4, color: 'text.disabled' }}>
          <Close fontSize="small" />
        </IconButton>
      )}

      {questionText && (
        <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.6, color: 'text.secondary', mb: 1.5, pr: onClose ? 4 : 0 }}>
          {questionText}
        </Typography>
      )}

      <Typography
        sx={{
          fontSize: '0.6875rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.8px',
          color: 'text.disabled',
          mb: 0.5,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
        }}
      >
        <Lock sx={{ fontSize: 14 }} /> Set the Combination
      </Typography>

      <Typography sx={{ fontSize: '0.6875rem', color: 'text.disabled', mb: 2 }}>
        Swipe each dial up or down
      </Typography>

      {/* Roller columns */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 1,
          mb: 2,
        }}
      >
        {digits.map((digit, index) => (
          <RollerColumn
            key={index}
            index={index}
            digit={digit}
            setDigitAt={setDigitAt}
            disabled={isSubmitting}
          />
        ))}
      </Box>

      {/* Reset + Submit buttons */}
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mb: 1 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RestartAlt />}
          onClick={handleReset}
          disabled={isSubmitting}
          sx={{
            borderColor: 'divider',
            color: 'text.secondary',
            fontSize: '0.8125rem',
            textTransform: 'none',
            borderRadius: 2,
            px: 2,
          }}
        >
          Reset
        </Button>

        <ButtonBase
          onClick={handleSubmit}
          disabled={!hasInteracted || isSubmitting}
          sx={{
            background: hasInteracted
              ? 'linear-gradient(135deg, #d4a853, #c4943f)'
              : 'rgba(128,128,128,0.15)',
            color: hasInteracted ? '#1a1a2e' : 'text.disabled',
            borderRadius: 2,
            px: 3,
            py: 1,
            fontSize: '0.875rem',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            boxShadow: hasInteracted ? '0 2px 8px rgba(212,168,83,0.3)' : 'none',
            transition: 'all 0.15s',
          }}
        >
          {isSubmitting ? (
            <CircularProgress size={18} sx={{ color: '#1a1a2e' }} />
          ) : (
            <>
              <LockOpen sx={{ fontSize: 18 }} />
              Unlock
            </>
          )}
        </ButtonBase>
      </Box>

      {/* Skip / Cancel */}
      {(pendingInput.allowCancel || pendingInput.allowSkip) && (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 1 }}>
          {pendingInput.allowCancel && (
            <Button size="small" startIcon={<Cancel />} onClick={handleCancel} disabled={isSubmitting} sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
              {pendingInput.cancelButtonText || 'Cancel'}
            </Button>
          )}
          {pendingInput.allowSkip && (
            <Button size="small" startIcon={<SkipNext />} onClick={handleSkip} disabled={isSubmitting} sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
              {pendingInput.skipButtonText || 'Skip'}
            </Button>
          )}
        </Box>
      )}
    </Box>
  )
}

// --- Single Roller Column ---

interface RollerColumnProps {
  index: number
  digit: number
  setDigitAt: (index: number, value: number) => void
  disabled: boolean
}

function RollerColumn({ index, digit, setDigitAt, disabled }: RollerColumnProps) {
  const prevDigit = (digit - 1 + 10) % 10
  const nextDigit = (digit + 1) % 10

  const scrollUp = useCallback(() => {
    if (disabled) return
    setDigitAt(index, (digit - 1 + 10) % 10)
  }, [disabled, setDigitAt, index, digit])

  const scrollDown = useCallback(() => {
    if (disabled) return
    setDigitAt(index, (digit + 1) % 10)
  }, [disabled, setDigitAt, index, digit])

  const onStep = useCallback((dir: 1 | -1) => {
    setDigitAt(index, (digit + dir + 10) % 10)
  }, [setDigitAt, index, digit])

  const { containerRef, handleWheel } = useFlickInertia({
    onStep,
    itemCount: 10,
    disabled,
  })

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowUp') { e.preventDefault(); scrollUp() }
    else if (e.key === 'ArrowDown') { e.preventDefault(); scrollDown() }
  }, [scrollUp, scrollDown])

  const digitCellSx = {
    fontSize: '1.125rem',
    fontWeight: 500,
    fontFamily: "'Inter', sans-serif",
    fontVariantNumeric: 'tabular-nums' as const,
    opacity: 0.4,
    py: 0.75,
    cursor: disabled ? 'default' : 'pointer',
    userSelect: 'none' as const,
    transition: 'opacity 0.15s',
    color: 'text.primary',
    '&:hover': disabled ? {} : { opacity: 0.6 },
  }

  return (
    <Box
      ref={containerRef}
      tabIndex={0}
      role="spinbutton"
      aria-label={`Digit ${index + 1}, current value ${digit}`}
      aria-valuenow={digit}
      aria-valuemin={0}
      aria-valuemax={9}
      onKeyDown={handleKeyDown}
      onWheel={handleWheel}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(21,32,56,0.6)' : 'rgba(0,0,0,0.04)',
        borderRadius: 2.5,
        width: 52,
        overflow: 'hidden',
        outline: 'none',
        '&:focus-visible': {
          boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}`,
        },
      }}
    >
      {/* Top — click to scroll up */}
      <ButtonBase
        onClick={scrollUp}
        disabled={disabled}
        sx={digitCellSx}
        tabIndex={-1}
        aria-hidden
      >
        {prevDigit}
      </ButtonBase>

      {/* Center — current digit */}
      <Box
        sx={{
          fontSize: '1.5rem',
          fontWeight: 700,
          fontFamily: "'Inter', sans-serif",
          fontVariantNumeric: 'tabular-nums',
          color: 'text.primary',
          py: 1,
          px: 1.5,
          mx: 0.5,
          borderTop: 2,
          borderBottom: 2,
          borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(212,168,83,0.6)' : 'primary.main',
          width: '100%',
          textAlign: 'center',
          userSelect: 'none',
        }}
      >
        {digit}
      </Box>

      {/* Bottom — click to scroll down */}
      <ButtonBase
        onClick={scrollDown}
        disabled={disabled}
        sx={digitCellSx}
        tabIndex={-1}
        aria-hidden
      >
        {nextDigit}
      </ButtonBase>
    </Box>
  )
}
